<template>
  <v-dialog
    :value="value"
    max-width="400"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        補充
      </v-card-title>

      <v-card-text>
        <div class="mb-4">
          <div class="text-subtitle-2 grey--text">
            商品名
          </div>
          <div class="text-body-1">
            {{ itemName }}
          </div>
        </div>

        <div class="mb-4">
          <div class="text-subtitle-2 grey--text">
            現在の在庫
          </div>
          <div class="text-body-1">
            {{ formatCurrentDisplay }}
          </div>
        </div>

        <v-form ref="form">
          <!-- 個数管理の場合 -->
          <template v-if="isUnitPiece">
            <v-text-field
              v-model.number="quantity"
              label="補充数 *"
              type="number"
              step="1"
              min="1"
              :rules="[rules.required, rules.positiveInteger]"
              outlined
              dense
              suffix="個"
            />
          </template>

          <!-- 杯数管理の場合: ボトル補充 -->
          <template v-else>
            <v-card
              outlined
              class="pa-4"
            >
              <div class="text-subtitle-2 mb-3">
                ボトル補充
              </div>
              <div class="text-body-2 mb-3">
                1本あたり: {{ servingsPerBottle }}杯
              </div>
              <v-text-field
                v-model.number="bottleQuantity"
                label="補充本数 *"
                type="number"
                step="1"
                min="1"
                :rules="[rules.required, rules.positiveInteger]"
                outlined
                dense
                suffix="本"
                :hint="bottleQuantity ? `→ ${bottleQuantity * servingsPerBottle}杯追加` : ''"
                persistent-hint
              />
            </v-card>
          </template>
        </v-form>

        <v-alert
          v-if="previewDisplay"
          type="info"
          dense
          text
          class="mt-4"
        >
          補充後の在庫: {{ previewDisplay }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="close"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSaving"
          @click="save"
        >
          補充
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import InventoryItemApi from '@/api/v2/inventory/InventoryItem'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    inventoryItem: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const quantity = ref(null)
    const bottleQuantity = ref(null)
    const isSaving = ref(false)

    // 商品名
    const itemName = computed(() => {
      return props.inventoryItem ? props.inventoryItem.itemName : ''
    })

    // 現在の在庫数
    const currentQuantity = computed(() => {
      return props.inventoryItem ? props.inventoryItem.currentQuantity : 0
    })

    // 個数管理かどうか
    const isUnitPiece = computed(() => {
      return !props.inventoryItem || props.inventoryItem.unitType === 'piece'
    })

    // 1本あたり杯数
    const servingsPerBottle = computed(() => {
      if (!props.inventoryItem) return 0
      const capacity = Number(props.inventoryItem.bottleCapacityMl) || 0
      const serving = Number(props.inventoryItem.servingSizeMl) || 0
      if (capacity === 0 || serving === 0) return 0

      return Math.floor(capacity / serving)
    })

    // 現在のボトル本数
    const currentBottles = computed(() => {
      if (servingsPerBottle.value === 0) return 0

      return Math.floor(currentQuantity.value / servingsPerBottle.value)
    })

    // 現在の在庫表示
    const formatCurrentDisplay = computed(() => {
      if (isUnitPiece.value) {
        return `${currentQuantity.value}個`
      }

      return `${currentQuantity.value}杯（${currentBottles.value}本）`
    })

    // 補充後の在庫プレビュー
    const previewDisplay = computed(() => {
      if (!props.inventoryItem) return null

      if (isUnitPiece.value) {
        if (quantity.value === null || quantity.value === '') return null
        const newQty = currentQuantity.value + Number(quantity.value)

        return `${newQty}個`
      }
      // 杯数管理
      if (bottleQuantity.value === null || bottleQuantity.value === '') return null
      const addServings = Number(bottleQuantity.value) * servingsPerBottle.value
      const newQty = currentQuantity.value + addServings
      const newBottles = Math.floor(newQty / servingsPerBottle.value)

      return `${newQty}杯（${newBottles}本）`
    })

    // バリデーションルール
    const rules = {
      required: v => !!v || '必須項目です',
      positiveInteger: v => {
        if (v === null || v === '') return true
        const num = Number(v)

        return (Number.isInteger(num) && num > 0) || '1以上の整数を入力してください'
      },
    }

    // ダイアログ開閉時のリセット
    watch(() => props.value, newVal => {
      if (newVal) {
        quantity.value = null
        bottleQuantity.value = null
      }
    })

    const close = () => {
      emit('input', false)
    }

    const save = async () => {
      if (!vm.$refs.form.validate()) return

      let apiQuantity
      if (isUnitPiece.value) {
        if (!Number.isInteger(quantity.value)) {
          vm.$toast.error('補充数は整数で入力してください')

          return
        }
        apiQuantity = quantity.value
      } else {
        if (!Number.isInteger(bottleQuantity.value)) {
          vm.$toast.error('補充本数は整数で入力してください')

          return
        }
        // ボトル本数 × 1本あたり杯数 = 追加杯数
        apiQuantity = bottleQuantity.value * servingsPerBottle.value
      }

      isSaving.value = true

      try {
        await InventoryItemApi.replenishment(props.inventoryItem.id, {
          quantity: apiQuantity,
        })

        vm.$toast.success('補充しました')
        emit('saved')
        close()
      } catch (error) {
        const message = error?.response?.data?.message || '補充に失敗しました'
        vm.$toast.error(message)
      } finally {
        isSaving.value = false
      }
    }

    return {
      quantity,
      bottleQuantity,
      isSaving,
      itemName,
      currentQuantity,
      isUnitPiece,
      servingsPerBottle,
      currentBottles,
      formatCurrentDisplay,
      previewDisplay,
      rules,
      close,
      save,
    }
  },
}
</script>
